import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  useGetProviderListQuery,
  useGetPhoneNumberAvailableQuery,
  usePostPhoneNumberPurchaseMutation,
  getProviderLabel,
  getNumberDisplay,
  formatPhoneDisplay,
  COUNTRY_BY_PROVIDER,
  formatAvailableNumberLocation,
} from '@circuitry-ai/doc-data';
import { checkType, CtyButton, CtyShowToast } from '@circuitry-ai/doc-ui';

export interface PurchasePhoneNumberSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchaseSuccess?: () => void;
  ownedNumberSet?: Set<string>;
}

export const PurchasePhoneNumberSidePanel: React.FC<
  Readonly<PurchasePhoneNumberSidePanelProps>
> = ({ isOpen, onClose, onPurchaseSuccess, ownedNumberSet = new Set() }) => {
  const { t } = useTranslation();
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [purchasingNumber, setPurchasingNumber] = useState<string | null>(null);

  const { data: providerList } = useGetProviderListQuery();
  const providersForPurchase = useMemo(
    () =>
      (providerList?.providers ?? []).filter(
        (p) =>
          p && p !== 'all'
      ),
    [providerList]
  );

  const countryIso = selectedProvider
    ? COUNTRY_BY_PROVIDER[selectedProvider.toLowerCase()] ?? 'US'
    : 'US';
  const { data: availableData, isLoading: availableLoading, refetch: refetchAvailable } =
    useGetPhoneNumberAvailableQuery(
      { provider: selectedProvider ?? '', country_iso: countryIso },
      { skip: !selectedProvider }
    );

     const [postPurchase, { isLoading: purchaseMutationLoading }] =
    usePostPhoneNumberPurchaseMutation();

  const availableNumbers = availableData?.numbers ?? [];

  const handlePurchase = async (number: string) => {
    const normalized = number.replace(/\D/g, '');
    if (ownedNumberSet.has(normalized)) {
      CtyShowToast(checkType.info, t('You already own this number.'));
      return;
    }
    setPurchasingNumber(number);
    const country = selectedProvider
      ? COUNTRY_BY_PROVIDER[selectedProvider.toLowerCase()] ?? 'US'
      : 'US';
    try {
      await postPurchase({
        provider: selectedProvider!,
        country_iso: country,
        body: { number: number.trim() },
      }).unwrap();
      CtyShowToast(checkType.success, t('Number purchased successfully.'));
      onPurchaseSuccess?.();
      refetchAvailable();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? String(
              (err as { data?: { detail?: string } }).data?.detail ??
                t('Purchase failed')
            )
          : t('Purchase failed');
      CtyShowToast(checkType.error, message);
    } finally {
      setPurchasingNumber(null);
    }
  };

  const handleClose = () => {
    setSelectedProvider(null);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50"
            onClick={handleClose}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClose();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={t('Close')}
          />
          <motion.div
            key="panel"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 z-50 h-full w-full max-w-lg border-l border-divider bg-background overflow-y-auto shadow-xl flex flex-col"
          >
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-divider bg-background px-6 py-4">
              <h2 className="text-lg font-semibold text-foreground">
                {t('Purchase a Phone Number')}
              </h2>
              <Button
                isIconOnly
                size="sm"
                variant="light"
                onPress={handleClose}
                aria-label={t('Close')}
              >
                <Icon icon="mdi:close" className="w-5 h-5" />
              </Button>
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col gap-6 p-6">
              {/* Select Provider */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('Select Provider')}
                </label>
                <div className="flex flex-col gap-2">
                  {providersForPurchase.map((p) => {
                    const prov = p;
                    const isSelected = selectedProvider === prov;
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setSelectedProvider(prov)}
                        className={`flex items-center justify-between w-full p-4 rounded-xl border-2 text-left transition-colors ${
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-default-200 hover:border-default-300 bg-default-50/50'
                        }`}
                      >
                        <span className="font-medium text-foreground">
                          {getProviderLabel(p)}
                        </span>
                        <span
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            isSelected
                              ? 'border-primary bg-primary'
                              : 'border-default-300'
                          }`}
                        >
                          {isSelected && (
                            <span className="w-2 h-2 rounded-full bg-white shrink-0" />
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Available Numbers */}
              {selectedProvider && (
                <div className="flex flex-col gap-2 flex-1 min-h-0">
                  <label className="block text-sm font-medium text-foreground">
                    {t('Available Numbers')}
                  </label>
                  {availableLoading && (
                    <div className="flex items-center justify-center py-12">
                      <Spinner size="lg" />
                    </div>
                  )}
                  {!availableLoading && availableNumbers.length === 0 && (
                    <div className="py-8 text-center text-default-500 text-sm">
                      {t('No numbers available right now. Try refreshing.')}
                    </div>
                  )}
                  {!availableLoading && availableNumbers.length > 0 && (
                    <div className="flex flex-col gap-2 overflow-y-auto max-h-[50vh] pr-1">
                      {availableNumbers.map((phone) => {
                        const num = getNumberDisplay(phone);
                        const normalized = num.replace(/\D/g, '');
                        const isOwned = ownedNumberSet.has(normalized);
                        const isPurchasingThis =
                          purchasingNumber != null &&
                          (num === purchasingNumber ||
                            normalized ===
                              (purchasingNumber || '').replace(/\D/g, ''));
                        const location = formatAvailableNumberLocation(phone);
                        let purchaseLabel = t('Purchase');
                        if (isPurchasingThis) purchaseLabel = t('Purchasing…');
                        else if (isOwned) purchaseLabel = t('Already owned');
                        return (
                          <div
                            key={num}
                            className="flex items-center justify-between gap-4 p-4 rounded-xl border border-default-200 bg-default-50/50"
                          >
                            <div className="min-w-0">
                              <p className="font-semibold text-foreground">
                                {formatPhoneDisplay(num)}
                              </p>
                              <p className="text-sm text-default-500 truncate">
                                {location}
                              </p>
                            </div>
                            <CtyButton
                              size="sm"
                              color={isOwned ? 'default' : 'primary'}
                              onPress={() => handlePurchase(num)}
                              isDisabled={isOwned || isPurchasingThis}
                              isLoading={isPurchasingThis}
                            >
                              {purchaseLabel}
                            </CtyButton>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 flex justify-end border-t border-divider bg-background p-4">
              <Button
                variant="flat"
                color="default"
                onPress={handleClose}
                isDisabled={purchaseMutationLoading}
              >
                {t('Cancel')}
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PurchasePhoneNumberSidePanel;
